// Same formulas as calculateWFSFee / calculateReferralFee from your other
// project's utils.js — copied verbatim so this module is self-contained.

export const calculateWFSFee = (dimensions, weight, options = {}) => {
    try {
        if (!dimensions || !weight) return 0.00;
        const { length, width, height } = dimensions;
        const { isApparel = false, isHazardous = false, retailPrice = null } = options;

        if (!length || !width || !height || length <= 0 || width <= 0 || height <= 0 || weight <= 0) {
            return 0.00;
        }

        const sortedDimensions = [length, width, height].sort((a, b) => b - a);
        const [longestSide, medianSide, shortestSide] = sortedDimensions;
        const girth = 2 * (medianSide + shortestSide);
        const longestPlusGirth = longestSide + girth;

        if (weight > 150 || longestSide > 108 || longestPlusGirth > 165) return 0.00;

        let oversizeFee = 0;
        let baseShippingWeight = weight;

        if ((longestSide > 48 && longestSide <= 96) || medianSide > 30 ||
            (longestPlusGirth > 105 && longestPlusGirth <= 130)) {
            oversizeFee = 3.00;
        } else if ((longestSide > 96 && longestSide <= 108) ||
            (longestPlusGirth > 130 && longestPlusGirth <= 165)) {
            oversizeFee = 20.00;
            baseShippingWeight = Math.max(weight, 90);
        }

        let shippingWeight;
        if (weight < 1) {
            shippingWeight = Math.ceil(weight + 0.25);
        } else {
            const dimWeight = (length * width * height) / 139;
            shippingWeight = Math.ceil(Math.max(baseShippingWeight, dimWeight) + 0.25);
        }

        let baseFee = 0;
        if (shippingWeight <= 1) baseFee = 3.45;
        else if (shippingWeight === 2) baseFee = 4.95;
        else if (shippingWeight === 3) baseFee = 5.45;
        else if (shippingWeight >= 4 && shippingWeight <= 20) baseFee = 5.75 + (0.40 * (shippingWeight - 4));
        else if (shippingWeight >= 21 && shippingWeight <= 30) baseFee = 15.55 + (0.40 * (shippingWeight - 21));
        else if (shippingWeight >= 31 && shippingWeight <= 50) baseFee = 14.55 + (0.40 * (shippingWeight - 31));
        else if (shippingWeight >= 51) baseFee = 17.55 + (0.40 * (shippingWeight - 51));

        let additionalFees = 0;
        if (isApparel) additionalFees += 0.50;
        if (isHazardous) additionalFees += 0.50;
        if (retailPrice !== null && retailPrice < 10) additionalFees += 1.00;
        additionalFees += oversizeFee;

        return Math.round((baseFee + additionalFees) * 100) / 100;
    } catch {
        return 0.00;
    }
};

export const calculateReferralFee = (salePrice, category, subCategory = '') => {
    try {
        const price = parseFloat(salePrice);
        if (!price || price <= 0) return 0.00;
        const cat = category?.toLowerCase() || '';
        const subCat = subCategory?.toLowerCase() || '';

        if (cat.includes('apparel') || cat.includes('clothing') || cat.includes('accessories')) {
            if (price <= 15) return price * 0.05;
            if (price <= 20) return price * 0.10;
            return price * 0.15;
        }
        if ((cat.includes('appliances') && cat.includes('compact')) || subCat.includes('compact appliance')) {
            return price <= 300 ? price * 0.12 : 300 * 0.12 + (price - 300) * 0.08;
        }
        if (cat.includes('appliances') && cat.includes('major')) return price * 0.08;
        if (cat.includes('automotive') || cat.includes('powersports') || cat.includes('auto')) {
            return (subCat.includes('tires') || subCat.includes('wheels')) ? price * 0.10 : price * 0.12;
        }
        if (cat.includes('baby')) return price <= 10 ? price * 0.08 : price * 0.15;
        if (subCat.includes('base power tools')) return price * 0.12;
        if (cat.includes('beauty') || cat.includes('health') || cat.includes('personal care') || cat.includes('care')) {
            return price <= 10 ? price * 0.08 : price * 0.15;
        }
        if (cat.includes('books') || cat.includes('book')) return price * 0.15;
        if (cat.includes('camera') || cat.includes('photo') || subCat.includes('camera')) return price * 0.08;
        if (cat.includes('collectibles') || cat.includes('collectible')) return price * 0.08;
        if (cat.includes('consumer electronics') || cat.includes('electronics')) return price * 0.08;
        if (cat.includes('electronics accessories') || subCat.includes('electronics accessories')) {
            return price <= 100 ? price * 0.15 : 100 * 0.15 + (price - 100) * 0.08;
        }
        if (cat.includes('home') || cat.includes('kitchen') || cat.includes('decor') || cat.includes('garden')) return price * 0.15;
        if (cat.includes('grocery') || cat.includes('food')) return price <= 15 ? price * 0.08 : price * 0.15;
        if (cat.includes('furniture') || subCat.includes('furniture')) {
            return price <= 200 ? price * 0.15 : 200 * 0.15 + (price - 200) * 0.10;
        }
        if (cat.includes('industrial') || cat.includes('scientific')) return price * 0.12;
        if (cat.includes('jewelry') || cat.includes('precious metals')) {
            return price <= 250 ? price * 0.20 : 250 * 0.20 + (price - 250) * 0.05;
        }
        if (cat.includes('luggage') || cat.includes('travel')) return price * 0.15;
        if (cat.includes('music') && !cat.includes('instruments')) return price * 0.15;
        if (cat.includes('musical instruments') || cat.includes('instruments')) return price * 0.12;
        if (cat.includes('office')) {
            return (subCat.includes('printer') || subCat.includes('cartridge')) ? price * 0.12 : price * 0.15;
        }
        if (subCat.includes('outdoor power tools') || (cat.includes('tools') && subCat.includes('outdoor'))) {
            return price <= 500 ? price * 0.15 : price * 0.08;
        }
        if (cat.includes('outdoors') || cat.includes('sports')) {
            if (['hunting trail monitors', 'binoculars', 'telescopes', 'spotting scopes', 'night vision']
                .some((k) => subCat.includes(k))) return price * 0.08;
            return price * 0.15;
        }
        if (cat.includes('personal computers') || cat.includes('computers') || subCat.includes('computers')) return price * 0.06;
        if (cat.includes('pet')) return price * 0.15;
        if (cat.includes('plumbing') || cat.includes('heating') || cat.includes('cooling') || cat.includes('ventilation')) return price * 0.10;
        if (cat.includes('shoes') || cat.includes('handbags') || cat.includes('backpacks') || cat.includes('sunglasses')) return price * 0.15;
        if (cat.includes('software') || (cat.includes('video games') && cat.includes('computer'))) return price * 0.15;
        if (cat.includes('tires') || cat.includes('wheels')) return price * 0.10;
        if (cat.includes('tools') || cat.includes('home improvement')) return price * 0.15;
        if (cat.includes('toys') || cat.includes('games')) return price * 0.15;
        if (cat.includes('video') || cat.includes('dvd')) return price * 0.15;
        if (cat.includes('video game consoles') || (cat.includes('gaming') && cat.includes('console'))) return price * 0.08;
        if (cat.includes('watches') || cat.includes('watch')) {
            return price <= 1500 ? price * 0.15 : 1500 * 0.15 + (price - 1500) * 0.03;
        }
        if (cat.includes('cell phones') || cat.includes('phone')) return price * 0.08;
        if (subCat.includes('auto electronics')) return price * 0.08;
        return price * 0.15;
    } catch {
        // Any unexpected error (bad category type, etc.) falls back to the
        // same conservative 15% default used when category is simply
        // missing — never silently zero out the fee.
        const price = parseFloat(salePrice);
        return (!price || price <= 0) ? 0.00 : price * 0.15;
    }
};

const FEE_SETTINGS_KEY = 'walmart_batch_fee_settings';

export const getWalmartFeeSettings = () => {
    try { return JSON.parse(localStorage.getItem(FEE_SETTINGS_KEY)) || {}; }
    catch { return {}; }
};

export const saveWalmartFeeSettings = (settings) => {
    localStorage.setItem(FEE_SETTINGS_KEY, JSON.stringify(settings));
};

// Mirrors the backend's _extract_pack_details_from_batch_payload so the
// frontend can pull weight/dimensions straight out of a batch_item's
// stored payload without another network round-trip.
export const extractPackDetailsFromPayload = (payload) => {
    if (!payload) return { weight: null, length: null, width: null, height: null };

    const tradeEach = payload?.TradeItem?.each;
    if (tradeEach) {
        return {
            weight: tradeEach.eachWeight ?? null,
            length: tradeEach.eachDepth ?? null,
            width: tradeEach.eachWidth ?? null,
            height: tradeEach.eachHeight ?? null,
        };
    }

    const visible = payload?.Visible || {};
    const productTypeBlock = Object.values(visible)[0] || {};
    const measure = (key) => productTypeBlock?.[key]?.measure ?? null;

    return {
        weight: payload?.Orderable?.ShippingWeight ?? null,
        length: measure('assembledProductLength') ?? measure('assembledProductDepth'),
        width: measure('assembledProductWidth'),
        height: measure('assembledProductHeight'),
    };
};

export const extractPriceFromPayload = (payload) => payload?.Orderable?.price ?? null;

export const estimateWalmartProfit = ({
    salePrice, costPrice, extraCosts = 0, weight, dimensions,
    category, subCategory = '', isWFS,
}) => {
    const settings = getWalmartFeeSettings();
    const referralFee = calculateReferralFee(salePrice, category, subCategory);
    const wfsFee = isWFS ? calculateWFSFee(dimensions, weight, { retailPrice: salePrice }) : 0;
    const fulfillmentCost = isWFS ? 0 : (parseFloat(settings.fulfillmentCost) || 0);
    const inboundShipping = isWFS ? (parseFloat(settings.inboundShipping) || 0) * (weight || 0) : 0;
    // Sales tax applies ONLY to the actual Cost Price (what you paid for the
    // item) — never to manual add-on costs like Prep Cost or COGS, which
    // aren't purchase-taxable line items.
    const salesTax = (costPrice || 0) * ((parseFloat(settings.salesTax) || 0) / 100);

    const totalCostBasis = (costPrice || 0) + (extraCosts || 0);
    const totalFees = referralFee + wfsFee + fulfillmentCost + inboundShipping + salesTax;
    const profit = (salePrice || 0) - totalCostBasis - totalFees;
    const roi = totalCostBasis > 0 ? (profit / totalCostBasis) * 100 : 0;

    return { referralFee, wfsFee, fulfillmentCost, inboundShipping, salesTax, totalCostBasis, totalFees, profit, roi };
};